import { useCallback, useEffect, useState } from 'react';
import { Filter } from 'lucide-react';
import { columnsMatch } from '../../lib/list-query';

const STOP_MS = 400;

export function useColumnSearch() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState<Record<string, string>>({});
  useEffect(() => {
    const timer = window.setTimeout(() => setApplied(draft), STOP_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);
  const set = useCallback((key: string, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);
  const clear = useCallback(() => {
    setDraft({});
    setApplied({});
  }, []);
  const toggle = useCallback(() => setOpen((value) => !value), []);
  const matches = useCallback((cells: Record<string, string>) => columnsMatch(cells, applied), [applied]);
  return { open, toggle, draft, set, applied, clear, matches };
}

export function ColumnFilterToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={open}
      aria-label="Search columns"
      title="Search columns"
      onClick={onClick}
      className={`rounded p-1 ${open ? 'bg-[#895CF6] text-white' : 'text-[var(--bo-faint)] hover:bg-[var(--bo-hover)]'}`}
    >
      <Filter size={14} />
    </button>
  );
}

export function ColumnFilterInputs({ columns, draft, onChange, trailing = false }: {
  columns: { key: string; label: string }[];
  draft: Record<string, string>;
  onChange: (key: string, value: string) => void;
  trailing?: boolean;
}) {
  return (
    <tr className="normal-case tracking-normal">
      {columns.map((column) => (
        <th key={column.key} className="px-2 py-1.5 font-normal">
          <input
            aria-label={`Search ${column.label}`}
            value={draft[column.key] || ''}
            onChange={(event) => onChange(column.key, event.target.value)}
            className="w-full min-w-[4rem] rounded border border-[var(--bo-border)] bg-[var(--bo-surface)] px-2 py-1 text-xs font-normal normal-case tracking-normal text-[var(--bo-text)]"
          />
        </th>
      ))}
      {trailing ? <th className="px-2 py-1.5" /> : null}
    </tr>
  );
}
